import type { AnswerValue } from '@repo/contracts/cardfinder/funnel/types';
import type { BranchId } from '@/shared/config/funnel/constants';

export type ResolveLandingActionInput = {
  branchId: BranchId | null;
  isHydrated: boolean;
  isFirstStep: boolean;
  isBranchFirstStep: boolean;
  branchFromAnswers: AnswerValue;
  canReachCurrentStep: boolean;
};

export type LandingAction =
  | { kind: 'none' }
  | { kind: 'reset-branch'; branchId: BranchId }
  | { kind: 'redirect-to-entry' };

/** Pure landing policy: what arriving on a funnel step should do to the funnel session. */
export function resolveLandingAction({
  branchId,
  isHydrated,
  isFirstStep,
  isBranchFirstStep,
  branchFromAnswers,
  canReachCurrentStep,
}: ResolveLandingActionInput): LandingAction {
  if (!branchId || isFirstStep || !isHydrated) {
    return { kind: 'none' };
  }

  // Require a string primary_goal so `undefined !== branchId` cannot look like a mismatch.
  if (typeof branchFromAnswers === 'string' && branchFromAnswers !== branchId) {
    // Direct navigation onto another branch's first step (e.g. the entrypoint
    // banner jumping from one branch to another): override primary_goal instead
    // of wiping the whole session. Any other step keeps the guardrail below.
    if (isBranchFirstStep) {
      return { kind: 'reset-branch', branchId };
    }
    return { kind: 'redirect-to-entry' };
  }

  // Seed deep-linked branch entry.
  if (isBranchFirstStep && branchFromAnswers === undefined) {
    return { kind: 'reset-branch', branchId };
  }

  if (canReachCurrentStep) {
    return { kind: 'none' };
  }

  return { kind: 'redirect-to-entry' };
}
